import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { RootState } from "../store/reducers";
import toolsApi from "../store/modules/tools/toolsApi";

export type ToolName =
  | "searchMap"
  | "businessZones"
  | "analytics"
  | "repeatsReferrers"
  | "financialTrends"
  | "businessPlanning";

/** Settings > Tools, in display order. The defaults apply until the user's settings load. */
export const TOOLS: { name: ToolName; label: string; defaultEnabled: boolean }[] =
  [
    { name: "searchMap", label: "Search & Map", defaultEnabled: true },
    { name: "businessZones", label: "Business Zones", defaultEnabled: true },
    { name: "analytics", label: "Analytics", defaultEnabled: true },
    {
      name: "repeatsReferrers",
      label: "Repeats & Referrers",
      defaultEnabled: true,
    },
    { name: "financialTrends", label: "Financial Trends", defaultEnabled: true },
    {
      name: "businessPlanning",
      label: "Business Planning",
      defaultEnabled: true,
    },
  ];

export const TOOLS_QUERY_KEY = "tools";

/** The user's tool switches (per user, so impersonation gets its own) */
export const useTools = () => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  return useQuery({
    queryKey: [TOOLS_QUERY_KEY, currentUser?.userId],
    queryFn: () => toolsApi.getTools(),
    enabled: !!currentUser?.userId,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};

/** Whether a tool is on, from the loaded settings or its default */
export const isToolEnabled = (
  tools: { name?: string; enabled?: boolean }[] | undefined,
  name: ToolName,
) =>
  tools?.find((tool) => tool.name === name)?.enabled ??
  TOOLS.find((tool) => tool.name === name)!.defaultEnabled;
